/**
 * PublishProgressDrawer.tsx — PLAT-10703 (follow-up to PLAT-10689 / PLAT-10009)
 *
 * Persistent, corner-docked publish progress widget. It appears the instant the
 * user clicks Publish — the ExportMenu calls `beginPendingPublish(count)` and
 * the drawer shows a "Preparing" bar while the fan-out's records are created.
 * Once a publish fan-out is dispatched (or restored on refresh via
 * `seedForStory`), `PublishTrackingStore` auto-watches the group and the live
 * bar takes over. Collapsed, the drawer is maximally compact: one line —
 * title + "F of T · pct%" summary — above a thin progress bar, with an
 * expand/collapse control revealing every queued variant, grouped by clip name
 * then language.
 * The content itself lives in `publishProgressContent`, shared with the
 * in-modal `PublishProgressHeaderStrip` so the two can't drift.
 *
 * The ExportMenu closes immediately on Publish — this drawer owns all progress
 * UI. It is a pure observer of the store: the RootLayout-level Dato realtime
 * subscription (`GlobalQuerySubscriptionComponent`) keeps the records live no
 * matter which modal/tab/route is active. All drawer state (watched groups,
 * expanded) is store-backed because `RootLayout` remounts per route.
 *
 * Surface handoff (PLAT-10947): while the clip-preview modal is open its header
 * strip registers as a header host and this drawer stands down, so progress
 * never renders twice. Close the modal and the drawer takes over again, with
 * the same live state and its floating presentation unchanged.
 *
 * Layering: portaled to `document.body` at `PUBLISH_PROGRESS_DRAWER_Z`
 * (1000035) — above the whole clip-preview modal stack (≤1000030), below the
 * transient `TOP_LAYER_Z` overlays (1000040+).
 *
 * Position (PLAT-11083): grab the panel anywhere and drag — on release it snaps
 * to the corner of the quadrant the cursor is in (`usePublishDrawerDrag`), and
 * `PublishTrackingStore.drawerCorner` remembers it across routes, story switches,
 * dismissal and reloads. The whole panel is the drag surface, so the grab cursor
 * is on the panel and the controls inside it (caret, Close, the per-variant
 * actions) are excluded from the gesture rather than carved out visually. No corner is
 * unoccupied — which is why this is a preference and not a third global default —
 * so each anchor only clears the chrome it *must*, and only where that chrome
 * exists: **in Creator Studio** the top pair drop below the 52px toolbar so a
 * publish never blocks Save/Create, while every other route keeps the flush 16px
 * inset. The bottom pair sit above the Pylon support launcher everywhere, so
 * support stays reachable without the `display:none` hack bottom-right once
 * needed.
 *
 * A persistent Close (X) button sits beside the expand/collapse control at
 * every stage (PLAT-10796): collapsing only minimizes the drawer, so Close is
 * the clear way to exit the publish flow and return to the main interface. It
 * only hides the progress UI — the fan-out keeps running server-side. On
 * completion the drawer persists ("Complete") until dismissed.
 */

import { PUBLISH_PROGRESS_DRAWER_Z } from '@src/components/common/topLayerZ';
import { useSidebarState } from '@src/hooks/useSidebarState';
import { useStore } from '@src/stores-v2/StoreContext';
import { Studio } from '@src/types.ts/general';
import { getCurrentStudio } from '@src/utility/general';
import { AnimatePresence, motion } from 'framer-motion';
import { observer } from 'mobx-react-lite';
import { createPortal } from 'react-dom';
import { useLocation } from 'react-router-dom';
import styled, { css, keyframes } from 'styled-components';

import { SIDEBAR_SPRING } from '../sidebar/sidebarMotion';
import { usePublishDrawerDrag } from './hooks/usePublishDrawerDrag';
import {
  PublishProgressHeader,
  PublishVariantList,
  usePublishProgressView,
} from './publishProgressContent';

import type { PublishDrawerCorner } from '@src/stores-v2/PublishTrackingStore';

export const PublishProgressDrawer = observer(function PublishProgressDrawer() {
  const { publishTrackingStore } = useStore();
  const view = usePublishProgressView();
  // Left-docked corners must clear the nav rail, whose width animates.
  const { sidebarWidth } = useSidebarState();
  // Creator Studio is the only route with fixed top chrome to clear.
  const { pathname } = useLocation();
  const inCreatorStudio = getCurrentStudio(pathname) === Studio.creator;
  // Grab anywhere on the panel and drop it in the corner you want.
  const { dragging, offset, startDrag } = usePublishDrawerDrag(
    publishTrackingStore.setDrawerCorner,
  );

  // The in-modal header strip owns the surface while it's mounted.
  if (publishTrackingStore.hasHeaderHost) return null;
  if (!view.isVisible) return null;

  const corner = publishTrackingStore.drawerCorner;

  return createPortal(
    <Panel
      data-testid="publish-progress-drawer"
      data-corner={corner}
      data-dragging={dragging || undefined}
      role="status"
      onMouseDown={startDrag}
      $corner={corner}
      $dragging={dragging}
      $leftOffset={sidebarWidth + EDGE_GAP}
      $topOffset={inCreatorStudio ? CREATOR_TOOLBAR_TOP_OFFSET : EDGE_GAP}
      style={
        dragging
          ? { transform: `translate(${offset.dx}px, ${offset.dy}px)` }
          : undefined
      }
    >
      <PublishProgressHeader
        view={view}
        expanded={view.expanded}
        onToggleExpanded={publishTrackingStore.setDrawerExpanded}
      />

      <AnimatePresence initial={false}>
        {view.expanded && (
          <motion.div
            key="variants"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={SIDEBAR_SPRING}
            style={{ overflow: 'hidden' }}
          >
            <PublishVariantList view={view} />
          </motion.div>
        )}
      </AnimatePresence>
    </Panel>,
    document.body,
  );
});

const slideInFromTop = keyframes`
  from {
    transform: translateY(-16px);
    opacity: 0;
  }
  to {
    transform: translateY(0);
    opacity: 1;
  }
`;

const slideInFromBottom = keyframes`
  from {
    transform: translateY(16px);
    opacity: 0;
  }
  to {
    transform: translateY(0);
    opacity: 1;
  }
`;

/** Gap between the drawer and the edge (or the nav rail) it's docked against. */
const EDGE_GAP = 16;
/** In Creator Studio ONLY, top docks drop below the 52px `CreatorToolbar` so a
 *  publish never covers Download / Save / Create. Every other route keeps the
 *  flush 16px inset — no other studio has fixed top chrome to clear, and pushing
 *  the drawer down there would just read as detached. Hardcoded rather than
 *  imported from `VideoCreator` — a body-portaled global widget shouldn't pull a
 *  route component's module graph in, and `SaveStatusIndicator` hardcodes its own
 *  offset the same way. Keep in sync if the toolbar height changes. */
const CREATOR_TOOLBAR_TOP_OFFSET = 52 + EDGE_GAP;
/** Bottom docks clear the Pylon support launcher (`right: 24px; bottom: 16px`,
 *  ~56px tall) so support stays clickable mid-publish. Applied to BOTH bottom
 *  corners, not just the right one: flipping the drawer along an edge shouldn't
 *  also change its height. */
const BOTTOM_OFFSET = 88;

type AnchorOffsets = { top: number; left: number };

/* Each anchor pins exactly two edges and leaves the other two at `auto`, and
   picks the slide-in that starts from the edge it's docked to. Written as
   functions of the measured offsets rather than as CSS custom properties, so the
   computed values land directly on the element (a `var()` would be opaque to
   both `getComputedStyle` and the tests). */
const CORNER_ANCHORS: Record<
  PublishDrawerCorner,
  (offsets: AnchorOffsets) => ReturnType<typeof css>
> = {
  'top-right': ({ top }) => css`
    top: ${top}px;
    right: ${EDGE_GAP}px;
    animation-name: ${slideInFromTop};
  `,
  'top-left': ({ top, left }) => css`
    top: ${top}px;
    left: ${left}px;
    animation-name: ${slideInFromTop};
  `,
  'bottom-left': ({ left }) => css`
    bottom: ${BOTTOM_OFFSET}px;
    left: ${left}px;
    animation-name: ${slideInFromBottom};
  `,
  'bottom-right': () => css`
    bottom: ${BOTTOM_OFFSET}px;
    right: ${EDGE_GAP}px;
    animation-name: ${slideInFromBottom};
  `,
};

const Panel = styled.div<{
  $corner: PublishDrawerCorner;
  $dragging: boolean;
  $leftOffset: number;
  $topOffset: number;
}>`
  position: fixed;
  ${({ $corner, $topOffset, $leftOffset }) =>
    CORNER_ANCHORS[$corner]({ top: $topOffset, left: $leftOffset })}
  /* The whole panel is the drag handle, so the affordance is on the panel; the
     controls inside it set their own pointer cursor and are excluded from the
     gesture. */
  cursor: ${({ $dragging }) => ($dragging ? 'grabbing' : 'grab')};
  ${({ $dragging }) =>
    $dragging &&
    css`
      /* Follow the cursor via an inline transform: kill the entry keyframe and
         the left transition so neither fights it, and don't let a press-and-sweep
         select the panel's text. */
      animation: none;
      transition: none;
      user-select: none;
    `}
  z-index: ${PUBLISH_PROGRESS_DRAWER_Z};
  display: flex;
  flex-direction: column;
  width: 280px;
  /* Bottom-docked and expanded, the panel grows upward; cap it so the header —
     and therefore Close — can't leave the viewport under heavy zoom. No overflow
     here on purpose: it would clip the position picker. VariantScroll already
     caps and scrolls the list itself. */
  max-height: calc(100vh - ${BOTTOM_OFFSET + EDGE_GAP}px);
  background: var(--background);
  border: 1px solid var(--border);
  border-radius: 10px;
  /* Floating layer per the design system — --elevation-toast, never -sm. */
  box-shadow: var(--elevation-toast);
  padding: 10px;
  animation-duration: 0.25s;
  animation-timing-function: ease-out;
  /* Only left animates: collapsing the nav rail while left-docked should glide.
     A corner change flips top/bottom/right to auto, which isn't interpolable —
     transitioning those would jerk one axis while the other slid, and an instant
     move is the more predictable answer to an explicit "put it there" click. */
  transition: left 200ms ease;
`;
